import React from 'react'
import { Element, Button, Message } from 'react-bulma-components'
import { SimilarTagWarning, CreateTagConfirmation } from './types'

/**
 * Props for the create tag confirmation component
 */
export interface CreateTagConfirmationProps {
  /** Warning when similar tag is found */
  similarTagWarning?: SimilarTagWarning | null
  /** However, confirmation state for creating a new tag */
  createTagConfirmation?: CreateTagConfirmation | null
  /** Loading state for tag creation */
  isLoading: boolean
  /** Callback when canceling tag creation */
  onCancel: () => void
  /** Callback when confirming tag creation */
  onConfirmCreate: () => void
}

/**
 * Component for displaying tag creation confirmation or similarity warning
 * Shows a warning when a similar tag exists, or a confirmation when creating a new tag
 */
const CreateTagConfirmationComponent: React.FC<CreateTagConfirmationProps> = ({
  createTagConfirmation,
  isLoading,
  onCancel,
  onConfirmCreate,
}) => {
  const { similarTagWarning } = createTagConfirmation || {}
  // Render warning message for similar tags
  if (similarTagWarning) {
    return (
      <Message color="warning" mt={3} data-test="create-similar-tag-confirmation-message">
        <Message.Body>
          <Element>
            <strong>Attention :</strong> Le tag "{similarTagWarning.newTag}" est très similaire au
            tag existant "{similarTagWarning.similarTag}".
          </Element>
          <Element>Voulez-vous vraiment créer ce nouveau tag ?</Element>
          <Element display="flex" justifyContent="flex-end" mt={3}>
            <Button
              color="light"
              onClick={onCancel}
              mr={2}
              data-test="create-tag-confirmation-cancel-button"
            >
              Annuler
            </Button>
            <Button
              color="warning"
              type="button"
              onClick={onConfirmCreate}
              loading={isLoading}
              data-test="create-tag-confirmation-button"
            >
              Créer quand même
            </Button>
          </Element>
        </Message.Body>
      </Message>
    )
  }

  // Render confirmation message for new tags
  if (createTagConfirmation) {
    return (
      <Message color="info" mt={3} data-test="create-tag-confirmation-message">
        <Message.Body>
          <Element>
            Voulez-vous créer le nouveau tag "
            <Element renderAs="strong">{createTagConfirmation.newTag}</Element>" ?
          </Element>
          <Element display="flex" justifyContent="flex-end" mt={3}>
            <Button
              color="light"
              onClick={onCancel}
              mr={2}
              data-test="create-tag-confirmation-cancel-button"
            >
              Annuler
            </Button>
            <Button
              color="primary"
              type="button"
              onClick={onConfirmCreate}
              loading={isLoading}
              data-test="create-tag-confirmation-button"
            >
              Créer le tag
            </Button>
          </Element>
        </Message.Body>
      </Message>
    )
  }

  // Return null if no confirmation or warning is needed
  return null
}

export default CreateTagConfirmationComponent
